import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import './Categories.css';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(ENDPOINT, {
        params: { action: 'getCategories' },
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setCategories(response.data.data || []);
      } else {
        throw new Error(response.data.message || 'Failed to load categories');
      }
    } catch (error) {
      console.error('Fetch categories error:', error);
      toast.error(error.response?.data?.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save category
  const saveCategory = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    try {
      const token = localStorage.getItem('token');
      const action = editingId ? 'updateCategory' : 'addCategory';
      const data = editingId ? { ...form, id: editingId } : form;
      
      const response = await axios.post(
        `${ENDPOINT}?action=${action}`,
        data,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        toast.success(editingId ? 'Category updated successfully' : 'Category added successfully');
        setIsModalOpen(false);
        setForm({ name: '', description: '' });
        setEditingId(null);
        fetchCategories();
      } else {
        throw new Error(response.data.message || 'Failed to save category');
      }
    } catch (error) {
      console.error('Save category error:', error);
      toast.error(error.response?.data?.message || 'Failed to save category');
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${ENDPOINT}?action=deleteCategory`,
          { id },
          {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data.success) {
          toast.success('Category deleted successfully');
          fetchCategories();
        } else {
          throw new Error(response.data.message || 'Failed to delete category');
        }
      } catch (error) {
        console.error('Delete category error:', error);
        toast.error(error.response?.data?.message || 'Failed to delete category');
      }
    }
  };

  // Open modal for editing
  const openEditModal = (category) => {
    setForm({
      name: category.name,
      description: category.description || ''
    });
    setEditingId(category.id);
    setIsModalOpen(true);
  };

  // Reset form and close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setForm({ name: '', description: '' });
    setEditingId(null);
    setErrors({});
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="categories-container">
      <div className="categories-header">
        <h2>Product Categories</h2>
        <button 
          className="add-category-btn"
          onClick={() => setIsModalOpen(true)}
        >
          <FaPlus /> Add Category
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="empty-state">
          <p>No categories found. Add your first category to get started.</p>
        </div>
      ) : (
        <div className="categories-grid">
          {categories.map(category => (
            <div key={category.id} className="category-card">
              <div className="category-content">
                <h3>{category.name}</h3>
                {category.description && <p>{category.description}</p>}
              </div>
              <div className="category-actions">
                <button 
                  className="icon-btn edit-btn"
                  onClick={() => openEditModal(category)}
                  title="Edit category"
                >
                  <FaEdit />
                </button>
                <button 
                  className="icon-btn delete-btn"
                  onClick={() => deleteCategory(category.id)}
                  title="Delete category"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Category Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Edit Category' : 'Add New Category'}</h3>
              <button className="close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={saveCategory}>
              <div className="form-group">
                <label htmlFor="name">Category Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'input-error' : ''}
                  placeholder="Enter category name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={form.description}
                  onChange={handleInputChange}
                  placeholder="Enter description (optional)"
                  rows="3"
                ></textarea>
              </div>
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                >
                  {editingId ? 'Update' : 'Save'} Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
